import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlay } = FiIcons;

const LazyYouTubeEmbed = ({ videoId, title, className = '' }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  if (!videoId) {
    return null;
  }

  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;

  const handlePlayClick = () => {
    setIsLoaded(true);
  };

  return (
    <div 
      className={`lazy-youtube-container ${className}`}
      style={{
        position: 'relative',
        width: '100%',
        paddingBottom: '56.25%', // 16:9 aspect ratio
        height: 0,
        overflow: 'hidden',
        borderRadius: '16px',
        background: '#000',
        cursor: isLoaded ? 'default' : 'pointer'
      }}
      onClick={!isLoaded ? handlePlayClick : undefined}
    >
      {!isLoaded ? (
        <>
          {/* Thumbnail Image */}
          <img
            src={thumbnailUrl}
            alt={`YouTube video thumbnail: ${title}`}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'all 0.3s ease'
            }}
            onError={(e) => {
              // Fallback to standard definition thumbnail if maxres fails
              e.target.src = `https://img.youtube.com/vi/${videoId}/sddefault.jpg`;
            }}
          />
          
          {/* Dark Overlay */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'rgba(0, 0, 0, 0.3)',
              transition: 'all 0.3s ease'
            }}
            className="youtube-overlay"
          />
          
          {/* Play Button */}
          <motion.div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '80px',
              height: '80px',
              background: 'rgba(255, 0, 0, 0.9)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 8px 32px rgba(255, 0, 0, 0.3)',
              backdropFilter: 'blur(10px)',
              border: '3px solid rgba(255, 255, 255, 0.2)'
            }}
            whileHover={{ 
              scale: 1.1,
              background: 'rgba(255, 0, 0, 1)',
              boxShadow: '0 12px 40px rgba(255, 0, 0, 0.4)'
            }}
            whileTap={{ scale: 0.95 }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ 
              type: "spring", 
              stiffness: 260, 
              damping: 20,
              delay: 0.2 
            }}
          >
            <SafeIcon 
              icon={FiPlay} 
              style={{ 
                fontSize: '32px', 
                color: '#ffffff',
                marginLeft: '4px' // Slight offset to center the play icon visually
              }} 
            />
          </motion.div>
          
          {/* Video Title Overlay */}
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.8))',
              padding: '40px 20px 20px 20px',
              color: '#ffffff'
            }}
          >
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              margin: 0,
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.7)',
              lineHeight: '1.3'
            }}>
              {title}
            </h3>
          </div>
          
          {/* Loading Indicator (appears on hover) */}
          <div
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'rgba(0, 0, 0, 0.7)',
              borderRadius: '8px',
              padding: '8px 12px',
              fontSize: '12px',
              color: '#ffffff',
              fontWeight: '500',
              opacity: 0,
              transition: 'opacity 0.3s ease'
            }}
            className="click-to-play"
          >
            Click to Play
          </div>
        </>
      ) : (
        /* YouTube Iframe */
        <motion.iframe
          src={embedUrl}
          title={title}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: '16px'
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        />
      )}
      
      <style jsx>{`
        .lazy-youtube-container:hover .youtube-overlay {
          background: rgba(0, 0, 0, 0.1) !important;
        }
        
        .lazy-youtube-container:hover .click-to-play {
          opacity: 1 !important;
        }
        
        @media (max-width: 768px) {
          .lazy-youtube-container {
            border-radius: 12px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default LazyYouTubeEmbed;